import styled from 'styled-components';
import BackButton from '../utils/back_button';
import { new_educator_blog_1 } from "../../../assets/images";
import { useNavigate } from 'react-router-dom';
import OtherBlogs from '../our_blogs/OtherBlogs';
import CTASection from "../../landing/landing_home/empower";

export default function Blog23() {

  return (
    <>
     <Fullpage>
      <PageBackground>
        <BlogContainer>
          <BlogWrapper>
            <BlogHeader
              title="Faculty at the Frontline of the AI Shift"
              date="09 April 2026"
              author="posted by campus insights"
            />
            <BlogContent />
          </BlogWrapper>
        </BlogContainer>
        <OtherBlogs currentBlogId={23} type="ai-lms" />
     </PageBackground>
     </Fullpage>
     <CTASection />
    </>
  );
};

const BlogHeader = ({ title, date, author }) => {
  const navigate = useNavigate();
  const handleBackClick = () => {
    window.scrollTo(0, 0);
    navigate("/blog");
  };
  return (
    <HeaderContainer>
      <HeaderContent>
        <BackButtonWrapper>
          <BackButton onClick={handleBackClick} />
        </BackButtonWrapper>
        <MainTitle>{title}</MainTitle>
        <MetadataContainer>
          <MetadataWrapper>
            <MetadataContent>
              <DateText>{date}</DateText>
              <AuthorText>{author}</AuthorText>
            </MetadataContent>
          </MetadataWrapper>
        </MetadataContainer>
      </HeaderContent>
      <HeroImage src={new_educator_blog_1} />
      <IntroText>
        Artificial intelligence is no longer a distant concept in higher education—it's already in the classroom, the curriculum, and the careers your students are preparing for. Faculty are at the center of this transformation, and Campus Insights' LXP (Learning eXperience Platform) is designed to make that position a source of strength, not stress.
      </IntroText>
    </HeaderContainer>
  );
};

const BlogContent = () => {
  return (
    <ContentContainer>
      <BlogSection title="Teaching With AI, Not Around It" ismainsection="true">
        <ContentText>
          Many educators worry that AI will complicate their work or make their expertise seem less relevant. The opposite is true. When faculty have access to an intelligent LMS, they gain a platform that amplifies their teaching—handling routine administrative tasks, surfacing student performance data, and offering adaptive content delivery—so instructors can focus on what they do best: mentoring, facilitating critical thinking, and inspiring curiosity. The LXP doesn't replace faculty judgment; it informs it.
        </ContentText>
      </BlogSection>

      <BlogSection title="Course Offerings That Strengthen Every Discipline" ismainsection="true">
        <ContentText>
          The platform's course library isn't just for students. Faculty can integrate AI-aligned modules directly into their curricula, helping students across every discipline develop the skills employers are now prioritizing. Whether you teach business, the humanities, health sciences, or engineering, there are relevant offerings to embed:
        </ContentText>
        <BulletList>
          <BulletItem>AI in [Your Discipline]: Applications and Implications</BulletItem>
          <BulletItem>Data Literacy for Non-Technical Majors</BulletItem>
          <BulletItem>Critical Evaluation of AI-Generated Content</BulletItem>
          <BulletItem>Workforce Readiness in an Automated Economy</BulletItem>
          <BulletItem>Research Methods Enhanced by AI Tools</BulletItem>
          <BulletItem>Ethics and Governance in Technology-Driven Fields</BulletItem>
        </BulletList>
        <ContentText>
          These courses are designed to complement existing syllabi, not compete with them. Faculty retain full instructional authority while offering students a richer, more future-ready learning experience.
        </ContentText>
      </BlogSection>

      <BlogSection title="Reducing Job Displacement Through Education" ismainsection="true">
        <ContentText>
          One of the most pressing responsibilities facing faculty today is preparing students for a labor market being reshaped by automation. The LXP helps educators address this directly by building student competencies that remain resilient as technology evolves:
        </ContentText>
        <BulletList>
          <BulletItem>Adaptability and continuous learning habits</BulletItem>
          <BulletItem>Human-centered communication and collaboration</BulletItem>
          <BulletItem>Complex problem-solving that AI cannot replicate alone</BulletItem>
          <BulletItem>Cross-disciplinary thinking and creative application</BulletItem>
          <BulletItem>Ethical reasoning in technology-influenced environments</BulletItem>
        </BulletList>
        <ContentText>
          By embedding these competencies throughout the curriculum, faculty become active participants in protecting their students' career prospects—not passive observers of a changing job market.
        </ContentText>
      </BlogSection>

      <BlogSection title="Supporting Faculty, Not Adding to Their Workload" ismainsection="true">
        <ContentText>
          Campus Insights understands that faculty are already stretched thin. That's why the LXP is built to reduce administrative burden, not add to it. Key features designed with faculty time in mind include:
        </ContentText>
        <BulletList>
          <BulletItem>Automated grading and progress reporting for objective assessments</BulletItem>
          <BulletItem>AI-generated insights on class-wide performance trends</BulletItem>
          <BulletItem>Early-alert systems that flag at-risk students before they fall behind</BulletItem>
          <BulletItem>Streamlined course setup with reusable content templates</BulletItem>
          <BulletItem>Integrated communication tools that reduce inbox overload</BulletItem>
        </BulletList>
        <ContentText>
          The result is more time for meaningful instruction and less time managing logistics—which is exactly what great teaching requires.
        </ContentText>
      </BlogSection>

      <BlogSection title="A Platform That Evolves With You" ismainsection="true">
        <ContentText>
          The AI landscape will continue to change, and so will the demands placed on faculty. Campus Insights' LXP is built to evolve alongside both—regularly updated with new course content, emerging best practices, and platform improvements informed by real faculty feedback. You won't be left behind as technology advances; you'll be equipped to lead your institution forward.
        </ContentText>
        <ContentText>
          Faculty aren't just adapting to the AI shift. With the right tools, they're shaping it. Campus Insights is here to make sure you have everything you need to do exactly that.
        </ContentText>
      </BlogSection>
    </ContentContainer>
  );
};

const BlogSection = ({ title, children, ismainsection = "false" }) => {
  return (
    <SectionContainer>
      {title && (
        <SectionTitle ismainsection={ismainsection}>
          {title}
        </SectionTitle>
      )}
      <SectionContent>{children}</SectionContent>
    </SectionContainer>
  );
};

const BulletList = styled.ul`
  margin: 0;
  padding-left: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BulletItem = styled.li`
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
`;

const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
`;

const SectionTitle = styled.h2`
  align-self: stretch;
  color: ${props => props.ismainsection === "true" ? '#1c1b1f' : '#64636a'};
  letter-spacing: ${props => props.ismainsection === "true" ? '-0.32px' : '-0.2px'};
  font: ${props => props.ismainsection === "true" ? '700 32px/120% Inter, sans-serif' : '500 20px/140% Inter, sans-serif'};
  margin: 0;
  @media (max-width: 640px) {
    font-size: ${props => props.ismainsection === "true" ? '24px' : '18px'};
  }
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  align-self: stretch;
`;

const ContentText = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;
`;

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const MainTitle = styled.h1`
  width: 100%;
  color: #1c1b1f;
  letter-spacing: -0.48px;
  font: 700 48px/120% Inter, sans-serif;
  margin: 0;
  @media (max-width: 991px) {
    font-size: 36px;
    line-height: 120%;
  }
  @media (max-width: 640px) {
    font-size: 28px;
    line-height: 120%;
  }
`;

const MetadataContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const MetadataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
`;

const MetadataContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const DateText = styled.time`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const AuthorText = styled.span`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 500 16px/120% Inter, sans-serif;
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const HeroImage = styled.img`
  display: flex;
  width: 100%;
  height: 346px;
  justify-content: flex-end;
  align-items: center;
  border-radius: 16px;
  object-fit: cover;
  object-position: top;
  @media (max-width: 991px) {
    height: 280px;
  }
  @media (max-width: 640px) {
    height: 200px;
  }
`;

const IntroText = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;
`;

const BlogContainer = styled.article`
  display: flex;
  width: 1120px;
  padding: 56px;
  flex-direction: column;
  align-items: flex-start;
  gap: 64px;
  border-radius: 24px;
  margin: 0 auto;
  max-width: 100%;
  box-sizing: border-box;
  background-color: #fff;
  @media (max-width: 991px) {
    width: 100%;
    padding: 40px 32px;
    gap: 48px;
  }
  @media (max-width: 640px) {
    padding: 24px 16px;
    gap: 32px;
  }
`;

const BlogWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  align-self: stretch;
`;

const PageBackground = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 500px, #F5F5F9 100px, #F5F5F9 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 200px 0;
  flex-direction: column;

  @media (max-width: 640px) {
    padding: 40px 0;
    background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 100px, #ffffff 100px, #ffffff 100%);
  }
`;

const Fullpage = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  @media (max-width: 640px) {
    padding: 40px 0;
  }
`;
